import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Divider,
  Tooltip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { usePerformance } from "../../../hooks/features/usePerformance";
import { useAuth } from "../auth/AuthGuard";
import { API_ENDPOINTS, PERMISSIONS } from "../../../constants";
import useNotification from "../../../hooks/common/useNotification";
import useConfirmDialog from "../../../hooks/common/useConfirmDialog";
import ErrorBoundary from "../../common/ErrorBoundary";

const AppraisalList = ({ type = 'all', title = 'Performance Appraisals' }) => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { showConfirmDialog } = useConfirmDialog();
  
  const {
    appraisals,
    pagination,
    loading,
    fetchAppraisals,
    fetchMyAppraisals,
    fetchPendingReviews,
    deleteAppraisal,
    exportAppraisals
  } = usePerformance();

  // State for filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [quarterFilter, setQuarterFilter] = useState("");
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // State for actions menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);

  // Fetch data based on type
  useEffect(() => {
    const fetchData = async () => {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        status: statusFilter,
        quarter: quarterFilter,
        year: yearFilter
      };

      try {
        switch (type) {
          case 'my':
            await fetchMyAppraisals(params);
            break;
          case 'pending':
            await fetchPendingReviews(params);
            break;
          default:
            await fetchAppraisals(params);
            break;
        }
      } catch (error) {
        console.error('Failed to fetch appraisals:', error);
      }
    };

    fetchData();
  }, [type, page, rowsPerPage, searchTerm, statusFilter, quarterFilter, yearFilter]);

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'self_submitted':
        return 'info';
      case 'peer_reviewed':
        return 'warning';
      case 'hr_reviewed':
        return 'success';
      default:
        return 'default';
    }
  };

  // Status icon mapping
  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft':
        return <ScheduleIcon fontSize="small" />;
      case 'self_submitted':
        return <PendingIcon fontSize="small" />;
      case 'peer_reviewed':
        return <StarIcon fontSize="small" />;
      case 'hr_reviewed':
        return <CompletedIcon fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  // Handle actions menu
  const handleMenuOpen = (event, appraisal) => {
    setAnchorEl(event.currentTarget);
    setSelectedAppraisal(appraisal);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAppraisal(null);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedAppraisal) return;

    const confirmed = await showConfirmDialog(
      'Delete Appraisal',
      `Are you sure you want to delete this appraisal for ${selectedAppraisal.user?.first_name} ${selectedAppraisal.user?.sur_name}?`
    );

    if (confirmed) {
      try {
        await deleteAppraisal(selectedAppraisal.id);
        handleMenuClose();
      } catch (error) {
        console.error('Failed to delete appraisal:', error);
      }
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const params = {
        status: statusFilter,
        quarter: quarterFilter,
        year: yearFilter
      };
      await exportAppraisals(params);
    } catch (error) {
      console.error('Failed to export appraisals:', error);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm,
      status: statusFilter,
      quarter: quarterFilter,
      year: yearFilter
    };

    try {
      switch (type) {
        case 'my':
          await fetchMyAppraisals(params);
          break;
        case 'pending':
          await fetchPendingReviews(params);
          break;
        default:
          await fetchAppraisals(params);
          break;
      }
    } catch (error) {
      console.error('Failed to refresh appraisals:', error);
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtered and sorted appraisals
  const filteredAppraisals = useMemo(() => {
    let filtered = appraisals || [];
    
    if (searchTerm) {
      filtered = filtered.filter(appraisal => 
        appraisal.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        appraisal.user?.sur_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        appraisal.objective?.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [appraisals, searchTerm]);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage performance appraisals and track employee progress
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          {hasPermission(PERMISSIONS.PERFORMANCE_CREATE) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/performance/appraisals/create')}
            >
              Create Appraisal
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search appraisals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="self_submitted">Self Submitted</MenuItem>
                  <MenuItem value="peer_reviewed">Peer Reviewed</MenuItem>
                  <MenuItem value="hr_reviewed">HR Reviewed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Quarter</InputLabel>
                <Select
                  value={quarterFilter}
                  onChange={(e) => setQuarterFilter(e.target.value)}
                  label="Quarter"
                >
                  <MenuItem value="">All Quarters</MenuItem>
                  <MenuItem value="Q1">Q1</MenuItem>
                  <MenuItem value="Q2">Q2</MenuItem>
                  <MenuItem value="Q3">Q3</MenuItem>
                  <MenuItem value="Q4">Q4</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={yearFilter}
                onChange={(e) => setYearFilter(parseInt(e.target.value))}
                inputProps={{ min: 2020, max: 2030 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("");
                    setQuarterFilter("");
                    setYearFilter(new Date().getFullYear());
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Appraisals Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Objective</TableCell>
                <TableCell>Quarter</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Overall Score</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredAppraisals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No appraisals found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAppraisals.map((appraisal) => (
                  <TableRow key={appraisal.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {appraisal.user?.first_name} {appraisal.user?.sur_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {appraisal.user?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {appraisal.objective?.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={appraisal.objective?.implementation_quarter}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {appraisal.objective?.fiscalYear?.year}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(appraisal.status)}
                        label={appraisal.status?.replace('_', ' ').toUpperCase()}
                        size="small"
                        color={getStatusColor(appraisal.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {appraisal.overall_score ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <StarIcon fontSize="small" color="warning" />
                          <Typography variant="body2" fontWeight="medium">
                            {appraisal.overall_score}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(appraisal.created_at), 'dd/MM/yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, appraisal)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={pagination.totalItems || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate(`/performance/appraisals/${selectedAppraisal?.id}`);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        
        {hasPermission(PERMISSIONS.PERFORMANCE_UPDATE) && (
          <MenuItem
            onClick={() => {
              navigate(`/performance/appraisals/${selectedAppraisal?.id}/edit`);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        
        {hasPermission(PERMISSIONS.PERFORMANCE_DELETE) && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

// Wrapper component with error boundary
const AppraisalListWithErrorBoundary = (props) => {
  return (
    <ErrorBoundary
      fallback={
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              Unable to load appraisals
            </Typography>
            <Typography variant="body2">
              There was an error loading the appraisal data. Please try refreshing the page or contact support if the problem persists.
            </Typography>
          </Alert>
        </Box>
      }
    >
      <AppraisalList {...props} />
    </ErrorBoundary>
  );
};

export default AppraisalListWithErrorBoundary;
